import type { ReactNode } from 'react'
import { ShieldIcon } from '@/components/icons'
import { useAuth } from '@/stores/AuthProvider'
import type { Permission } from '@/types/api'

/**
 * 模块级的权限门。
 *
 * ## 为什么单独一个文件
 *
 * `App.tsx` 的路由表在模块顶层就构造出 `element` 的 JSX，因此这个组件必须**同步**
 * 可用。放在 `AdminLayout.tsx` 里就意味着整个管理后台外壳被拉进主 bundle，
 * 那一层懒加载（`lazy(() => import(...AdminLayout))`）随之失效 —— 而绝大多数访客
 * 永远不会打开后台。
 *
 * ## 为什么逐路由套一层，而不是在 AdminLayout 里统一判断
 *
 * 理由与后端逐路由挂中间件相同：改成「进了后台就都能进」再让各页面自己判断，
 * 等于把七处判断散落到七个文件里，漏一处的表现是页面正常打开、然后它发出的每个
 * 请求都 403、界面只剩一片红条 —— 而这恰好是最难从截图上认出的故障。
 *
 * ## 它不是安全边界
 *
 * 真正的门在后端：每条 `/admin/**` 都过 `requirePermission()` 或
 * `requireSuperAdmin()`。这一层的作用是不给人展示一个点下去必然失败的入口。
 */
export type PermissionNeed = Permission | 'super'

export function RequirePermission({
  need,
  children,
}: {
  need: PermissionNeed
  children: ReactNode
}) {
  const { can, isSuperAdmin } = useAuth()
  const allowed = need === 'super' ? isSuperAdmin : can(need)
  if (allowed) return <>{children}</>

  // 这里刻意**不是** 404。AdminLayout 对普通用户给 404 是为了不宣告后台的存在，
  // 但走到这一步的人已经在后台里了：他知道后台存在，也知道自己是管理员，
  // 一个 404 只会让他以为页面搬走了。所以给一句能照着做的话，
  // 与后端 403 的 detail 说的是同一件事。
  return (
    <section className="rounded-card border border-border bg-surface p-8 text-center">
      <ShieldIcon className="mx-auto size-8 text-muted-foreground" />
      <p className="mt-3 text-base font-semibold text-foreground">权限不足</p>
      <p className="mx-auto mt-1 max-w-md text-sm leading-6 text-muted-foreground">
        {need === 'super'
          ? '这个页面只有超级管理员可以访问。'
          : '你的账号没有被授予这个模块的权限，请联系超级管理员为你开通。'}
      </p>
    </section>
  )
}
